import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart, Users, ShieldCheck, Home as HomeIcon, Mail, LogIn,
  ArrowRight, MessageCircle, Sparkles, Phone, MapPin,
} from "lucide-react";

/**
 * Public landing page for NestVillage Care Metrics — premium residential support.
 *
 * Tone:  warm, residential, dignity-first community living.
 *        NOT clinical / hospital / nursing-home imagery.
 *
 * Design system:
 *   - Navy #163B66, Soft blue #5B8DEF, Warm white #F7F9FC, Light gray #EEF2F7
 *   - 24px rounded corners, soft shadows, generous spacing
 *   - Outfit (heading) / Manrope (body) — already set globally
 *
 * Image rules:
 *   - Composite imagery (uploaded by NestVillage) shown EXACTLY as provided.
 *   - object-contain everywhere full subjects must be preserved (no head/shoulder crops).
 *   - Soft cream background fill keeps letterboxing visually intentional.
 */

// Brand-curated composite imagery — served from /public, no external CDN.
const HERO_IMG       = "/lp-hero.png";       // 1717×916 caregiver + resident on couch
const ENV_IMG        = "/lp-home-env.png";   // 1536×1024 four-room collage
const TEAM_IMG       = "/lp-team.png";       // 1755×896 team meeting + 4 values row
const TRUST_IMG      = "/lp-trust.png";      // 771×1024 caregiver + 3 profile circles

// Official NestVillage contact details.
const CONTACT = {
  org: "NestVillage LLC",
  street: "11631 West Brown Street",
  cityState: "Youngtown, AZ 85363",
  phone: "602-596-5894",
  phoneHref: "tel:+16025965894",
  email: "contact@nestvillagellc.com",
  emailHref: "mailto:contact@nestvillagellc.com",
  mapsEmbed: "https://www.google.com/maps?q=11631+West+Brown+Street,+Youngtown,+AZ+85363&output=embed",
  mapsLink: "https://www.google.com/maps/search/?api=1&query=11631+West+Brown+Street,+Youngtown,+AZ+85363",
};

const FAMILY_REASONS = [
  {
    icon: Heart,
    title: "Compassionate Care",
    desc: "Person-centered support built around the dignity, goals, and preferences of each resident.",
  },
  {
    icon: HomeIcon,
    title: "Safe Home Environment",
    desc: "Welcoming, well-maintained homes with the comfort, supervision, and safety families expect.",
  },
  {
    icon: Users,
    title: "Experienced Staff",
    desc: "Background-checked, trained caregivers led by hands-on House Managers who know each resident.",
  },
  {
    icon: Sparkles,
    title: "Community Integration",
    desc: "Residents are active, valued members of their neighborhoods — not isolated from community life.",
  },
];

/** Lightweight fade-up-on-scroll wrapper (no animation library). */
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current || shown) return;
    const ob = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setShown(true),
      { threshold: 0.12 },
    );
    ob.observe(ref.current);
    return () => ob.disconnect();
  }, [shown]);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out will-change-transform ${shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      style={shown ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

function TrustBullet({ title, desc }) {
  return (
    <li className="flex gap-3" data-testid={`trust-bullet-${title.toLowerCase().replace(/[\s&]+/g, "-")}`}>
      <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-[#5B8DEF] shrink-0 ring-4 ring-[#5B8DEF]/15" />
      <div>
        <p className="font-heading font-semibold text-[#163B66] text-base">{title}</p>
        <p className="text-sm text-slate-600 leading-relaxed mt-0.5">{desc}</p>
      </div>
    </li>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#F7F9FC] text-slate-800" data-testid="landing-page">
      {/* ─── Header ─── */}
      <header className="bg-white/95 backdrop-blur sticky top-0 z-30 border-b border-[#EEF2F7]" data-testid="landing-header">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo-link">
            <img src="/nestvillage-logo-transparent.png" alt="NestVillage" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <p className="font-heading font-bold text-[#163B66] text-base sm:text-lg">NestVillage</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Residential Support</p>
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="#about" className="hidden sm:inline-flex items-center text-sm font-semibold text-slate-600 hover:text-[#163B66] transition-colors px-3 py-2" data-testid="landing-nav-about">About</a>
            <a href="#contact" className="hidden md:inline-flex items-center text-sm font-semibold text-slate-600 hover:text-[#163B66] transition-colors px-3 py-2" data-testid="landing-nav-contact">Contact</a>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 bg-[#163B66] hover:bg-[#0f2a4d] text-white text-sm font-semibold px-4 sm:px-5 py-2.5 rounded-full shadow-sm transition-colors"
              data-testid="landing-header-login-btn"
            >
              <LogIn size={14} /> <span className="hidden sm:inline">Secure Login</span><span className="sm:hidden">Login</span>
            </Link>
          </div>
        </div>
      </header>

      {/* ─── SECTION 1 — HERO ─── */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#EEF2F7] via-[#F7F9FC] to-white" />
        <div className="absolute -top-40 -right-32 w-[480px] h-[480px] rounded-full bg-[#5B8DEF]/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#5B8DEF] font-bold inline-flex items-center gap-2" data-testid="landing-hero-eyebrow">
              <HomeIcon size={13} /> Residential Support · Community Living
            </p>
            <h1
              className="mt-4 font-heading font-bold text-[#163B66] tracking-tight break-words"
              style={{ fontSize: "clamp(2.125rem, 4.4vw, 3.5rem)", lineHeight: 1.12, maxWidth: "720px" }}
              data-testid="landing-hero-headline"
            >
              Providing Compassionate Residential Support with{" "}
              <span className="text-[#5B8DEF]">Respect, Dignity, and Empathy.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed" data-testid="landing-hero-sub">
              Supporting individuals through safe, inclusive, and community-centered living environments — homes where people are known, valued, and supported to live their best lives.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#about" className="inline-flex items-center gap-2 bg-[#163B66] hover:bg-[#0f2a4d] text-white text-sm font-semibold px-6 py-3 rounded-full shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg" data-testid="landing-hero-learn-more">
                Learn More <ArrowRight size={14} />
              </a>
              <a href="#contact" className="inline-flex items-center gap-2 border-2 border-[#163B66]/15 bg-white hover:border-[#5B8DEF] hover:text-[#163B66] text-slate-700 text-sm font-semibold px-6 py-3 rounded-full transition-all hover:-translate-y-0.5" data-testid="landing-hero-contact">
                <MessageCircle size={14} /> Contact Us
              </a>
              <Link to="/login" className="inline-flex items-center gap-2 text-[#5B8DEF] hover:text-[#163B66] text-sm font-semibold px-3 py-3 transition-colors" data-testid="landing-hero-login-btn">
                <ShieldCheck size={14} /> Secure Login
              </Link>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="relative">
              {/* Hero image card — 24px rounded, soft shadow, full-subject preserved (object-contain on cream). */}
              <div
                className="rounded-[24px] overflow-hidden border border-white shadow-[0_24px_60px_-20px_rgba(22,59,102,0.35)] bg-[#F7F1E8]"
                data-testid="landing-hero-image-card"
              >
                <img
                  src={HERO_IMG}
                  alt="A NestVillage caregiver sharing a warm, supportive moment with a resident at home"
                  loading="eager"
                  className="w-full h-auto object-contain block"
                />
              </div>
              {/* Floating badge */}
              <div
                className="absolute -bottom-4 left-4 sm:-bottom-5 sm:left-8 bg-white rounded-2xl shadow-xl border border-[#EEF2F7] px-4 py-3 flex items-center gap-3"
                data-testid="landing-hero-badge"
              >
                <div className="bg-[#163B66] text-white rounded-full p-2.5">
                  <Heart size={16} className="fill-white text-white" />
                </div>
                <div>
                  <p className="font-heading font-bold text-[#163B66] text-sm leading-none">Person-centered Care</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mt-1">Residents come first</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── SECTION 2 — HOME ENVIRONMENT ─── */}
      <section id="about" className="py-20 sm:py-24" data-testid="landing-home-environment">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#5B8DEF] font-bold" data-testid="landing-env-eyebrow">
                Home Environment
              </p>
              <h2 className="mt-3 font-heading font-bold text-[#163B66] text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight" data-testid="landing-env-title">
                Spaces designed to feel like home.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
                Bright, clean, and welcoming environments designed for comfort, belonging, and safety.
              </p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div
              className="mt-12 sm:mt-16 rounded-[24px] overflow-hidden bg-white shadow-[0_30px_80px_-30px_rgba(22,59,102,0.25)] border border-[#EEF2F7]"
              data-testid="landing-env-image-card"
            >
              <img
                src={ENV_IMG}
                alt="NestVillage home environments — welcoming living rooms, open kitchens, cozy bedrooms, and outdoor patios"
                loading="lazy"
                className="w-full h-auto object-contain block"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── SECTION 3 — TEAM & CULTURE ─── */}
      <section className="bg-[#EEF2F7] py-20 sm:py-24" data-testid="landing-team">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#5B8DEF] font-bold" data-testid="landing-team-eyebrow">
                Team &amp; Culture
              </p>
              <h2 className="mt-3 font-heading font-bold text-[#163B66] text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight" data-testid="landing-team-title">
                A team culture built around support.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
                We invest in people, communication, compassion, and growth.
              </p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div
              className="mt-12 sm:mt-16 rounded-[24px] overflow-hidden bg-white shadow-[0_30px_80px_-30px_rgba(22,59,102,0.3)] border border-white"
              data-testid="landing-team-image-card"
            >
              <img
                src={TEAM_IMG}
                alt="NestVillage team meeting — we build, we support, we empower, we treat everyone like family"
                loading="lazy"
                className="w-full h-auto object-contain block"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── SECTION 4 — TRUST & QUALITY ─── */}
      <section className="py-20 sm:py-24" data-testid="landing-trust">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <div
              className="rounded-[24px] overflow-hidden bg-[#F7F9FC] shadow-[0_24px_60px_-20px_rgba(22,59,102,0.3)] border border-[#EEF2F7]"
              data-testid="landing-trust-image-card"
            >
              <img
                src={TRUST_IMG}
                alt="A NestVillage caregiver in a navy polo with team members below — a standard you can feel"
                loading="lazy"
                className="w-full h-auto object-contain block"
              />
            </div>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#5B8DEF] font-bold" data-testid="landing-trust-eyebrow">
              Safety, Trust &amp; Quality
            </p>
            <h2 className="mt-3 font-heading font-bold text-[#163B66] text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight" data-testid="landing-trust-title">
              A standard you can feel.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Background-checked, trained, and proud of the work they do — our caregivers are the heart of NestVillage. We don't just measure quality on paper; you see it in how every shift starts, every conversation unfolds, and every home feels.
            </p>
            <ul className="mt-8 space-y-4" data-testid="landing-trust-bullets">
              <TrustBullet title="Person-centered plans" desc="Every resident has a plan built around their goals, preferences, and dignity." />
              <TrustBullet title="24/7 support & supervision" desc="Trained caregivers on hand around the clock with hands-on leadership oversight." />
              <TrustBullet title="Family involvement" desc="Open communication with families and care teams at every step of the journey." />
              <TrustBullet title="Staff accountability" desc="Clear KPIs, audit trails, and AI-assisted insights keep quality measurably high." />
              <TrustBullet title="Quality-focused leadership" desc="House Managers and admins who lead with care, structure, and accountability." />
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ─── SECTION 5 — WHY FAMILIES CHOOSE NESTVILLAGE ─── */}
      <section className="bg-[#EEF2F7] py-20 sm:py-24" data-testid="landing-why-families">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#5B8DEF] font-bold">
                Why Families Choose NestVillage
              </p>
              <h2 className="mt-3 font-heading font-bold text-[#163B66] text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
                Care that feels like family, with the structure that earns trust.
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {FAMILY_REASONS.map((f, i) => (
              <Reveal key={f.title} delay={i * 90}>
                <div
                  className="group bg-white rounded-[24px] border border-white p-7 h-full shadow-[0_18px_40px_-24px_rgba(22,59,102,0.25)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(22,59,102,0.4)]"
                  data-testid={`landing-family-${f.title.toLowerCase().replace(/[\s&]+/g, "-")}`}
                >
                  <div className="bg-gradient-to-br from-[#163B66] to-[#5B8DEF] text-white rounded-2xl w-14 h-14 flex items-center justify-center shadow-lg shadow-[#5B8DEF]/30 transition-transform duration-300 group-hover:scale-110">
                    <f.icon size={24} strokeWidth={2} />
                  </div>
                  <h3 className="mt-6 font-heading font-bold text-[#163B66] text-lg">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GET IN TOUCH ─── */}
      <section id="contact" className="py-20 sm:py-24 bg-white" data-testid="landing-get-in-touch">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#5B8DEF] font-bold">
                Contact
              </p>
              <h2 className="mt-3 font-heading font-bold text-[#163B66] text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight" data-testid="landing-contact-title">
                Get in Touch
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
                We're here to answer questions, support families, and provide guidance about our residential support services.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-5 gap-6 items-stretch">
            {/* Contact cards (3 stacked on mobile / 3-col on tablet / 2-col block on desktop with map) */}
            <div className="lg:col-span-2 grid sm:grid-cols-3 lg:grid-cols-1 gap-5">
              <Reveal>
                <a
                  href={CONTACT.mapsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="group block bg-white rounded-[24px] border border-[#EEF2F7] p-6 h-full shadow-[0_18px_40px_-24px_rgba(22,59,102,0.18)] hover:shadow-[0_24px_56px_-22px_rgba(22,59,102,0.32)] hover:-translate-y-0.5 transition-all"
                  data-testid="contact-card-address"
                >
                  <div className="bg-gradient-to-br from-[#163B66] to-[#5B8DEF] text-white rounded-2xl w-12 h-12 flex items-center justify-center shadow-md shadow-[#5B8DEF]/30 transition-transform group-hover:scale-110">
                    <MapPin size={20} strokeWidth={2.2} />
                  </div>
                  <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-[#5B8DEF] font-bold">Visit us</p>
                  <p className="mt-2 font-heading font-semibold text-[#163B66] text-base leading-snug">{CONTACT.org}</p>
                  <p className="mt-1 text-sm text-slate-600">{CONTACT.street}</p>
                  <p className="text-sm text-slate-600">{CONTACT.cityState}</p>
                  <p className="mt-3 text-xs font-semibold text-[#5B8DEF] inline-flex items-center gap-1">
                    Open in Maps <ArrowRight size={12} />
                  </p>
                </a>
              </Reveal>
              <Reveal delay={80}>
                <a
                  href={CONTACT.phoneHref}
                  className="group block bg-white rounded-[24px] border border-[#EEF2F7] p-6 h-full shadow-[0_18px_40px_-24px_rgba(22,59,102,0.18)] hover:shadow-[0_24px_56px_-22px_rgba(22,59,102,0.32)] hover:-translate-y-0.5 transition-all"
                  data-testid="contact-card-phone"
                >
                  <div className="bg-gradient-to-br from-[#163B66] to-[#5B8DEF] text-white rounded-2xl w-12 h-12 flex items-center justify-center shadow-md shadow-[#5B8DEF]/30 transition-transform group-hover:scale-110">
                    <Phone size={20} strokeWidth={2.2} />
                  </div>
                  <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-[#5B8DEF] font-bold">Call us</p>
                  <p className="mt-2 font-heading font-semibold text-[#163B66] text-lg leading-snug">{CONTACT.phone}</p>
                  <p className="mt-1 text-sm text-slate-600">Click to call from any device.</p>
                </a>
              </Reveal>
              <Reveal delay={160}>
                <a
                  href={CONTACT.emailHref}
                  className="group block bg-white rounded-[24px] border border-[#EEF2F7] p-6 h-full shadow-[0_18px_40px_-24px_rgba(22,59,102,0.18)] hover:shadow-[0_24px_56px_-22px_rgba(22,59,102,0.32)] hover:-translate-y-0.5 transition-all"
                  data-testid="contact-card-email"
                >
                  <div className="bg-gradient-to-br from-[#163B66] to-[#5B8DEF] text-white rounded-2xl w-12 h-12 flex items-center justify-center shadow-md shadow-[#5B8DEF]/30 transition-transform group-hover:scale-110">
                    <Mail size={20} strokeWidth={2.2} />
                  </div>
                  <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-[#5B8DEF] font-bold">Email us</p>
                  <p className="mt-2 font-heading font-semibold text-[#163B66] text-base leading-snug break-all">{CONTACT.email}</p>
                  <p className="mt-1 text-sm text-slate-600">We typically respond within one business day.</p>
                </a>
              </Reveal>
            </div>

            {/* Embedded Google Maps */}
            <Reveal delay={120} className="lg:col-span-3">
              <div
                className="rounded-[24px] overflow-hidden border border-[#EEF2F7] shadow-[0_24px_60px_-22px_rgba(22,59,102,0.28)] bg-[#EEF2F7] h-full min-h-[320px] sm:min-h-[400px]"
                data-testid="contact-map"
              >
                <iframe
                  title="NestVillage LLC location map"
                  src={CONTACT.mapsEmbed}
                  className="w-full h-full min-h-[320px] sm:min-h-[400px] block"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── SECTION 6 — FOOTER ─── */}
      <footer className="bg-[#163B66] text-slate-200 pt-16 pb-8" data-testid="landing-footer">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-10 items-start pb-12 border-b border-white/10">
            <div>
              <div className="flex items-center gap-2.5">
                <img src="/nestvillage-logo-transparent.png" alt="" className="h-10 w-10 object-contain bg-white/10 rounded-xl p-1" />
                <div>
                  <p className="font-heading font-bold text-white text-lg">NestVillage</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#5B8DEF] font-semibold">Residential Support</p>
                </div>
              </div>
              <p className="mt-5 text-sm text-slate-300/90 max-w-xs leading-relaxed">
                Community-based residential support across our homes — built on respect, dignity, and empathy.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-flex items-center gap-2 bg-white text-[#163B66] hover:bg-[#5B8DEF] hover:text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
                data-testid="landing-footer-login-btn"
              >
                <LogIn size={14} /> Secure Login
              </Link>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#5B8DEF] font-bold">Contact</p>
              <ul className="mt-5 space-y-3 text-sm">
                <li className="flex items-start gap-3" data-testid="landing-footer-address">
                  <MapPin size={16} className="text-[#5B8DEF] mt-0.5 shrink-0" />
                  <span className="text-slate-300/90 leading-relaxed">
                    {CONTACT.org}<br />{CONTACT.street}<br />{CONTACT.cityState}
                  </span>
                </li>
                <li className="flex items-start gap-3" data-testid="landing-footer-phone">
                  <Phone size={16} className="text-[#5B8DEF] mt-0.5 shrink-0" />
                  <a href={CONTACT.phoneHref} className="hover:text-white transition-colors">{CONTACT.phone}</a>
                </li>
                <li className="flex items-start gap-3" data-testid="landing-footer-email">
                  <Mail size={16} className="text-[#5B8DEF] mt-0.5 shrink-0" />
                  <a href={CONTACT.emailHref} className="hover:text-white transition-colors break-all">
                    {CONTACT.email}
                  </a>
                </li>
              </ul>
            </div>

            <div className="md:text-right">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#5B8DEF] font-bold">Resources</p>
              <ul className="mt-5 space-y-3 text-sm">
                <li><a href="#about" className="hover:text-white transition-colors">About NestVillage</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors" data-testid="landing-footer-contact">Contact Us</a></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="landing-footer-privacy">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="landing-footer-terms">Terms of Use</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
            <p data-testid="landing-footer-copyright">© 2026 NestVillage LLC. All rights reserved.</p>
            <p className="text-slate-400/80" data-testid="landing-footer-tagline">Residential Support · Community Living · Care Metrics</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
